'use client'

import { PostHogProvider as PHProvider } from 'posthog-js/react'
import posthog from 'posthog-js'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, Suspense } from 'react'
import { setCookie, getCookie } from 'cookies-next'

// Initialize PostHog
if (typeof window !== 'undefined') {
  try {
    console.log('[PostHog] Starting initialization...');
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
      api_host: `${window.location.origin}/ingest`, // Use our proxy instead of direct PostHog URL
      capture_pageview: false, // We'll manually capture pageviews
      loaded: (posthog) => {
        try {
          // After PostHog is loaded, store the distinct ID in a cookie
          const distinctId = posthog.get_distinct_id();
          
          // Use cookies-next to set the cookie (works in both client and server components)
          setCookie('anonymous_user_id', distinctId, { 
            maxAge: 60 * 60 * 24 * 30, // 30 days
            path: '/',
            sameSite: 'lax'
          });
          
          console.log('[PostHog] Successfully initialized with distinct ID:', distinctId);
          
          if (process.env.NODE_ENV === 'development') {
            // Log PostHog events in development
            posthog.debug()
          }
        } catch (error) {
          console.error('[PostHog Error] Failed in loaded callback:', {
            error: error instanceof Error ? error.message : 'Unknown error',
            errorObject: error
          });
        }
      }
    })
  } catch (error) {
    console.error('[PostHog Error] Failed to initialize PostHog:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      errorObject: error
    });
  }
}

// Internal component that captures pageviews
function PostHogPageViewInternal(): React.ReactNode {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (pathname) {
      let url = window.origin + pathname
      if (searchParams?.toString()) {
        url = url + `?${searchParams.toString()}`
      }
      // Add the hash/fragment if it exists
      if (typeof window !== 'undefined' && window.location.hash) {
        url = url + window.location.hash
      }
      posthog.capture('$pageview', {
        $current_url: url,
      })
    }
  }, [pathname, searchParams])

  return null
}

// Exported component wrapped in Suspense to avoid deopting the app into client-side rendering
export function PostHogPageview(): React.ReactNode {
  return (
    <Suspense fallback={null}>
      <PostHogPageViewInternal />
    </Suspense>
  )
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return <PHProvider client={posthog}>{children}</PHProvider>
} 